import { Timestamp } from 'firebase/firestore';

// Admin User Types
export type AdminRole = 'super_admin' | 'admin' | 'editor';

export interface AdminUser {
  uid: string;
  email: string;
  displayName: string;
  role: AdminRole;
  active: boolean;
  createdAt: Timestamp;
  lastLogin?: Timestamp;
}

// Project Types
export type ProjectCategory = 'web_design' | 'mobile_app' | 'ai' | 'ecommerce' | 'ui_ux';

export interface Project {
  id: string;
  title: string;
  slug: string;
  category: ProjectCategory;
  year: string;
  description: string;
  heroImage: string;
  heroVideo?: string;
  technologies: string[];
  liveUrl?: string;
  gallery: string[];
  featured: boolean;
  order: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: string;
}

export interface ProjectFormData {
  title: string;
  slug: string;
  category: ProjectCategory;
  year: string;
  description: string;
  heroImage: string;
  heroVideo?: string;
  technologies: string[];
  liveUrl?: string;
  gallery: string[];
  featured: boolean;
  order: number;
}

// Service Types
export interface Service {
  id: string;
  title: string;
  description: string;
  icon: string;
  order: number;
  active: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface ServiceFormData {
  title: string;
  description: string;
  icon: string;
  order: number;
  active: boolean;
}

// Gallery Types
export interface GalleryItem {
  id: string;
  title: string;
  description?: string;
  imageUrl: string;
  category: string;
  order: number;
  active: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: string;
}

export interface GalleryFormData {
  title: string;
  description?: string;
  imageUrl: string;
  category: string;
  order: number;
  active: boolean;
}

// Site Settings Types
export interface SocialLink {
  platform: string;
  url: string;
}

export interface MapCoordinates {
  lat: number;
  lng: number;
}

export interface SiteSettings {
  siteName: string;
  siteDescription: string;
  email: string;
  emailSecondary?: string;
  phone: string;
  address: string;
  mapCoordinates: MapCoordinates;
  socialLinks: SocialLink[];
  googleAnalyticsId?: string;
  updatedAt: Timestamp;
  updatedBy: string;
}

// Application Types
export type ApplicationStatus = 'new' | 'reviewed' | 'contacted' | 'rejected';

export interface Application {
  id: string;
  position: string;
  name: string;
  email: string;
  phone: string;
  linkedin?: string;
  portfolio?: string;
  message?: string;
  cvUrl: string;
  cvFileName: string;
  status: ApplicationStatus;
  notes?: string;
  createdAt: Timestamp;
  reviewedBy?: string;
  reviewedAt?: Timestamp;
}

// Category Labels
export const PROJECT_CATEGORIES: Record<ProjectCategory, string> = {
  web_design: 'Web Design',
  mobile_app: 'Mobile App',
  ai: 'AI Integration',
  ecommerce: 'E-Commerce',
  ui_ux: 'UI/UX Design',
};

export const APPLICATION_STATUSES: Record<ApplicationStatus, string> = {
  new: 'New',
  reviewed: 'Reviewed',
  contacted: 'Contacted',
  rejected: 'Rejected',
};

// Position Types (Dynamic from Firestore)
export interface Position {
  id: string;
  value: string;
  label: string;
  active: boolean;
  order: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Fallback positions (used when Firestore is empty or loading)
export const DEFAULT_POSITIONS = [
  { value: 'frontend-developer', label: 'Frontend Developer' },
  { value: 'backend-developer', label: 'Backend Developer' },
  { value: 'fullstack-developer', label: 'Full Stack Developer' },
  { value: 'mobile-developer', label: 'Mobile Developer' },
  { value: 'ui-ux-designer', label: 'UI/UX Designer' },
  { value: 'graphic-designer', label: 'Graphic Designer' },
  { value: 'project-manager', label: 'Project Manager' },
  { value: 'marketing', label: 'Marketing Specialist' },
  { value: 'other', label: 'Other' },
];

// Keep for backwards compatibility
export const POSITION_OPTIONS = DEFAULT_POSITIONS;

export const ADMIN_ROLES: Record<AdminRole, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  editor: 'Editor',
};

// Article Types
export type ArticleStatus = 'draft' | 'published';

export interface Article {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  coverImage: string;
  category: string;
  tags: string[];
  author: string;
  authorImage?: string;
  status: ArticleStatus;
  readTime: number;
  featured: boolean;
  publishedAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: string;
}

export interface ArticleFormData {
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  coverImage: string;
  category: string;
  tags: string[];
  author: string;
  authorImage?: string;
  status: ArticleStatus;
  featured: boolean;
}

export const ARTICLE_STATUSES: Record<ArticleStatus, string> = {
  draft: 'Draft',
  published: 'Published',
};

// Article Category Types (Dynamic from Firestore)
export interface ArticleCategory {
  id: string;
  value: string;
  label: string;
  active: boolean;
  order: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Fallback article categories (used when Firestore is empty or loading)
export const DEFAULT_ARTICLE_CATEGORIES = [
  { value: 'technology', label: 'Technology' },
  { value: 'design', label: 'Design' },
  { value: 'development', label: 'Development' },
  { value: 'business', label: 'Business' },
  { value: 'news', label: 'News' },
  { value: 'tutorial', label: 'Tutorial' },
];

// Keep for backwards compatibility
export const ARTICLE_CATEGORIES = DEFAULT_ARTICLE_CATEGORIES;

// Client Types
export interface Client {
  id: string;
  name: string;
  url?: string;
  logo?: string;
  order: number;
  active: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: string;
}

export interface ClientFormData {
  name: string;
  url?: string;
  logo?: string;
  order: number;
  active: boolean;
}
